import './PageLayout.scss';

import { useEffect, useRef } from 'react';

import { DefaultRoutes } from 'Routes';
import Footer from 'components/Footer/Footer';
import { useLocation } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { TopNav } from 'components/TopNavigation/TopNavigation';
import { SideNavigation } from 'components/SideNavigation/SideNavigation';
import Box from '@mui/material/Box/Box';
import { useMenuStore } from 'stores/MenuStore';
import { getMatchingMenuItem, getMatchingTab, getMenuItemIdsRoutesMap, getTabsRoutesMap } from 'utilities/menuItems';
import { useTabsStore } from 'stores/TabsStore';
import { WhatsNewSingleStepModal } from 'components/WhatsNewModal/WhatsNewSingleStepModal/WhatsNewSingleStepModal';
import { WhatsNewStepperModal } from 'components/WhatsNewModal/WhatsNewStepperModal/WhatsNewStepperModal';
import { Grid } from '@mui/material';

type PageLayoutProps = {
  children?: React.ReactNode;
};

export const PageLayout = ({ children }: PageLayoutProps) => {
  const location = useLocation();

  const menuItemIdsRoutesMap = getMenuItemIdsRoutesMap();
  const tabsRoutesMap = getTabsRoutesMap();
  const { selectedMenuItem, setSelectedMenuItem } = useMenuStore();
  const { setActiveTabIndex } = useTabsStore();

  const isLandingPage = location.pathname === DefaultRoutes.LandingPage;
  const isNoAccessPage = location.pathname === DefaultRoutes.NoAccess;
  const isNotFoundPage = location.pathname === DefaultRoutes.NotFound;
  const isAdminPage = location.pathname.includes(DefaultRoutes.Admin);
  const showFooter = !isAdminPage && !isNoAccessPage && !isNotFoundPage;
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveTabIndex(0);
    contentRef.current?.scrollTo(0, 0);
    const matchingMenuItem = getMatchingMenuItem(menuItemIdsRoutesMap, location.pathname);

    if (matchingMenuItem) {
      setSelectedMenuItem(matchingMenuItem[0]);
    } else {
      setSelectedMenuItem('None');
    }

    const matchingTab = getMatchingTab(tabsRoutesMap, location.pathname);
    if (matchingTab !== undefined) {
      setActiveTabIndex(matchingTab);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (TagManager.dataLayer && !isAdminPage) {
      const timeout = setTimeout(() => {
        TagManager.dataLayer({
          dataLayer: {
            event: 'page_view',
            page_path: location.pathname + location.search,
            page_title: document.title,
          },
        });
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [location.pathname, isAdminPage]);

  return (
    <Grid container direction='column' height='100vh' padding={0} flexWrap='nowrap'>
      <Grid item>{!isLandingPage && !isNoAccessPage && !isNotFoundPage && <TopNav />}</Grid>
      <Grid item display='flex' flex={1} overflow='hidden'>
        <Box sx={{ overflowY: 'auto', flexShrink: 0 }}>
          {!isLandingPage && !isNoAccessPage && !isNotFoundPage && selectedMenuItem && <SideNavigation />}
        </Box>
        <Box ref={contentRef} sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', backgroundColor: '#F5F5F5' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 0 }}>{children}</Box>
          {showFooter && <Footer />}
        </Box>
      </Grid>
      {!isLandingPage && !isNoAccessPage && !isNotFoundPage && <WhatsNewStepperModal />}
      {!isLandingPage && !isNoAccessPage && !isNotFoundPage && <WhatsNewSingleStepModal />}
    </Grid>
  );
};
